/**
 * ReauthModal - Password Re-entry for Data Shield Reveal
 *
 * Presented when a user clicks "Reveal" on shielded data. After password
 * verification, the backend returns a time-limited reveal token (JWT) that
 * grants access to shielded fields. The token is stored in sessionStorage
 * and automatically attached to API requests via the apiClient interceptor.
 *
 * If the effective policy requires access justification, a reason text field
 * is also shown.
 */

import React, { useState } from 'react';
import { Shield, Lock, AlertCircle, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { useDataShield } from '@/contexts/DataShieldContext';
import axios from 'axios';

import { config as productConfig } from '@/src/config';
const API_BASE_URL = (productConfig.weaveinApi.baseUrl ?? '').trim();

export function ReauthModal() {
  const {
    showReauthModal,
    setShowReauthModal,
    activateRevealSession,
    effectivePolicy,
  } = useDataShield();

  const [password, setPassword] = useState('');
  const [justification, setJustification] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const requiresJustification = effectivePolicy?.require_access_justification ?? false;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!password) {
      setError('Password is required');
      return;
    }
    if (requiresJustification && !justification.trim()) {
      setError('Access justification is required by your organization policy');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.post(
        `${API_BASE_URL}/api/auth/verify-password`,
        {
          password,
          justification: justification.trim() || undefined,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
          },
        }
      );

      const { reveal_token, expires_at, effective_timeout_minutes } = response.data;

      activateRevealSession(reveal_token, expires_at, effective_timeout_minutes);

      // Reset form
      setPassword('');
      setJustification('');
      setError(null);
    } catch (err: any) {
      const detail = err.response?.data?.detail || 'Verification failed. Please try again.';
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setShowReauthModal(false);
    setPassword('');
    setJustification('');
    setError(null);
  };

  return (
    <Dialog open={showReauthModal} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <DialogTitle>Verify Identity</DialogTitle>
          </div>
          <DialogDescription>
            Re-enter your password to reveal shielded member data.
            {effectivePolicy && (
              <span className="block mt-1 text-xs">
                Access will expire in {effectivePolicy.reauth_timeout_minutes} minutes.
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Password Field */}
          <div>
            <label
              htmlFor="reauth-password"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                id="reauth-password"
                type="password"
                autoComplete="current-password"
                autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter your password"
                disabled={loading}
              />
            </div>
          </div>

          {/* Justification Field (if required by policy) */}
          {requiresJustification && (
            <div>
              <label
                htmlFor="reauth-justification"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Access Justification <span className="text-red-500">*</span>
              </label>
              <textarea
                id="reauth-justification"
                value={justification}
                onChange={(e) => setJustification(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                placeholder="Reason for accessing shielded data..."
                disabled={loading}
              />
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-sm text-red-700 dark:text-red-300">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Actions */}
          <DialogFooter>
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              disabled={loading || !password}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Verifying...
                </>
              ) : (
                'Reveal Data'
              )}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
